import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Trophy, Calendar, MapPin, ArrowRight, Dumbbell, Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export default function HibridoGamesRankingCard() {
  const { data: event, isLoading, error } = useQuery({
    queryKey: ["hibrido-games-ranking-card"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("events")
        .select("id, slug, name, status, location, start_date, end_date, description")
        .ilike("name", "%híbrido%")
        .order("created_at", { ascending: false })
        .limit(1);

      if (error) throw error;
      if (!data || data.length === 0) return null;
      return data[0];
    },
    retry: 3,
  });

  // Mostrar skeleton/placeholder enquanto carrega
  if (isLoading) {
    return (
      <div className="card-fpf3 p-5 md:p-6 animate-pulse">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <div className="h-6 bg-muted rounded w-1/3 mb-2" />
            <div className="h-4 bg-muted rounded w-1/2" />
          </div>
          <div className="h-10 bg-muted rounded w-32" />
        </div>
      </div>
    );
  }

  // Se não encontrou, mostrar card padrão com link para leaderboard
  if (!event) {
    return (
      <Link to="/leaderboard/hibrido-games-2026" className="group block">
        <div className="card-fpf3 p-5 md:p-6 hover:border-primary/30 transition-colors group">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <h3 className="text-lg font-bold">Hibrido Games 2026</h3>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider rounded-sm border bg-primary/10 text-primary border-primary/30">
                  <span className="w-1.5 h-1.5 rounded-full bg-current" />
                  Em Andamento
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider rounded-sm bg-primary/15 text-primary border border-primary/20">
                  <Trophy size={10} />
                  Ranking
                </span>
              </div>

              <div className="flex gap-4 mt-2">
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Dumbbell size={12} className="text-primary/60" />
                  Provas disponíveis
                </span>
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Users size={12} className="text-primary/60" />
                  Várias categorias
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0 flex-wrap w-full md:w-auto">
              <button className="px-3 py-2 text-xs font-semibold uppercase tracking-wider border border-primary/40 text-primary rounded-sm hover:bg-primary hover:text-primary-foreground transition-colors flex items-center gap-1.5 flex-1 md:flex-initial justify-center md:justify-start">
                <Trophy size={13} />
                Ver Leaderboard
              </button>
              <div className="flex-shrink-0 w-10 h-10 bg-primary/10 border border-primary/20 rounded-sm flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <ArrowRight size={16} className="text-primary group-hover:translate-x-0.5 transition-transform" />
              </div>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  const statusConfig = {
    published: { label: "Em Andamento", color: "bg-primary/10 text-primary border-primary/30" },
    active: { label: "Em Andamento", color: "bg-primary/10 text-primary border-primary/30" },
    completed: { label: "Finalizado", color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" },
    draft: { label: "Rascunho", color: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30" },
  };

  const status = statusConfig[event.status] || statusConfig.published;
  const startDate = event.start_date ? new Date(event.start_date + "T00:00:00") : null;
  const endDate = event.end_date ? new Date(event.end_date + "T00:00:00") : null;

  return (
    <Link
      to={`/leaderboard/${event.slug}`}
      className="group block"
    >
      <div className="card-fpf3 p-5 md:p-6 hover:border-primary/30 transition-colors group">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h3 className="text-lg font-bold truncate">{event.name}</h3>
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider rounded-sm border ${status.color}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-current" />
                {status.label}
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider rounded-sm bg-primary/15 text-primary border border-primary/20">
                <Trophy size={10} className="text-primary" />
                Ranking
              </span>
            </div>

            <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted-foreground">
              {startDate && (
                <span className="flex items-center gap-1.5">
                  <Calendar size={13} className="text-primary/60" />
                  {startDate.toLocaleDateString("pt-BR")}
                  {endDate && ` – ${endDate.toLocaleDateString("pt-BR")}`}
                </span>
              )}
              {event.location && (
                <span className="flex items-center gap-1.5">
                  <MapPin size={13} className="text-primary/60" />
                  {event.location}
                </span>
              )}
            </div>

            <div className="flex gap-4 mt-2">
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Dumbbell size={12} className="text-primary/60" />
                Provas disponíveis
              </span>
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Users size={12} className="text-primary/60" />
                Várias categorias
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0 flex-wrap w-full md:w-auto">
            <Link
              to={`/leaderboard/${event.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-2 text-xs font-semibold uppercase tracking-wider border border-primary/40 text-primary rounded-sm hover:bg-primary hover:text-primary-foreground transition-colors flex items-center gap-1.5 flex-1 md:flex-initial justify-center md:justify-start"
            >
              <Trophy size={13} className="text-primary" />
              Ver Leaderboard
            </Link>
            <div className="flex-shrink-0 w-10 h-10 bg-primary/10 border border-primary/20 rounded-sm flex items-center justify-center group-hover:bg-primary/20 transition-colors">
              <ArrowRight size={16} className="text-primary group-hover:translate-x-0.5 transition-transform" />
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
